import { Box, Button, Flex, Heading, Stack } from "@biblioteksentralen/react";
import { getPath, ResolvedEventSummary } from "@libry-content/common";
import { ResolvedService } from "@libry-content/common/src/services/groq/resolveService";
import { useState } from "react";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { EventsList } from "../arrangement/EventsList";
import { ArticleHeader } from "../ArticleHeader";
import { ServiceBadges } from "../Badge";
import BlockContent from "../blockContent/BlockContent";
import { DefaultContainer } from "../ContentContainer";
import InternalLink from "../InternalLink";
import { useCommonData } from "../layout/CommonDataProvider";
import { ContactCard } from "../employees/ContactCard";

interface Props {
  service?: ResolvedService;
  relatedEvents?: ResolvedEventSummary[];
}

export const ServiceView = ({ service, relatedEvents }: Props) => {
  const { ts } = useTranslation();
  const { site } = useCommonData();

  if (!service) return null;

  const moreThanOneLibraryTotal = site?.libraryOrdering && site.libraryOrdering.length > 1;

  return (
    <DefaultContainer>
      <Stack direction="column" spacing="1rem">
        <ArticleHeader
          title={ts(service.title)}
          teaser={ts(service.teaser)}
          badges={<ServiceBadges service={service} />}
          sanityImage={service.image}
          document={service}
        />
        <Flex
          direction={{ base: "column", md: "row" }}
          maxWidth={{ base: "30rem", md: "unset" }}
          margin={{ base: "auto", md: "unset" }}
          flexWrap="wrap"
          gap="3rem 2rem"
        >
          <Stack gap="3rem" flex={2} width="100%">
            <BlockContent blocks={ts(service.body)} />
            <RelatedEvents relatedEvents={relatedEvents} />
          </Stack>
          <Stack gap="3rem" flex={1.3} width="100%">
            <Contact service={service} />
            {moreThanOneLibraryTotal && <Libraries service={service} />}
          </Stack>
        </Flex>
      </Stack>
    </DefaultContainer>
  );
};

const Contact = (props: Pick<Props, "service">) => {
  const { service } = props;
  const { t } = useTranslation();

  if (!service?.contacts) return null;

  return (
    <Stack spacing="0.75rem">
      <Heading as="h2" size="md">
        {t("Kontakt")}
      </Heading>
      {service.contacts.map((contact) => (
        <ContactCard key={contact._id} contact={contact} hidePositionAndAffiliations />
      ))}
    </Stack>
  );
};

const Libraries = (props: Pick<Props, "service">) => {
  const { service } = props;
  const { ts, t } = useTranslation();

  if (!service?.libraries?.length) return null;

  return (
    <Stack>
      <Heading as="h2" size="md">
        {t("Bibliotek som tilbyr tjenesten")}:
      </Heading>
      <Stack as="ul" spacing="0.75rem">
        {service.libraries.map((library) => (
          <Box as="li" marginLeft="1rem" key={library._id}>
            <InternalLink href={getPath(library)}>{ts(library.name)}</InternalLink>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};

const RelatedEvents = (props: Pick<Props, "relatedEvents">) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState<boolean>(false);

  if (!props.relatedEvents?.length) return null;
  const eventsToShow = showAll ? props.relatedEvents : props.relatedEvents?.slice(0, 3);

  return (
    <Stack>
      <Heading as="h2" size="md">
        {t("Relaterte arrangementer")}
      </Heading>
      <EventsList events={eventsToShow} />
      {!showAll && props.relatedEvents.length > 3 && (
        <Button variant="secondary" onClick={() => setShowAll(true)}>
          {t("Se alle relaterte arrangementer")}
        </Button>
      )}
    </Stack>
  );
};
